import { useState } from "react";
import MacroBar from "../components/MacroBar";
import IngredientEditor from "../components/IngredientEditor";
import { inp, lbl, btn } from "../styles/shared";
import { MEALS, DAILY_DEFAULTS } from "../constants/config";
import { calcMacros } from "../utils/calculations";
import { fmtDate } from "../utils/formatting";
import { lookupNutrients } from "../constants/nutrients";

export default function MealLog({
  logs, saveLogs,
  activeMeal, setActiveMeal,
  logDate, setLogDate,
  liveMacros,
  getDayTotals, macroGoals, showToast,
  groceries,
  customFoods,
}) {

  // Projected full-day macros: logged meals use actual, unlogged use DAILY_DEFAULTS fallback
  const dayLog = logs.find(l => l.date === logDate);
  const projectedDay = MEALS.reduce((acc, meal) => {
    const logged  = dayLog?.meals?.[meal];
    const def     = DAILY_DEFAULTS[meal];
    const isEditing = activeMeal.meal === meal && activeMeal.ingredients?.length > 0;
    const source  = isEditing
      ? calcMacros(activeMeal.ingredients)
      : logged?.macros
      ? logged.macros
      : def?.ingredients ? calcMacros(def.ingredients)
      : null;
    if (!source) return acc;
    return {
      calories: acc.calories + (source.calories || 0),
      protein:  acc.protein  + (source.protein  || 0),
      carbs:    acc.carbs    + (source.carbs     || 0),
      fat:      acc.fat      + (source.fat       || 0),
      fiber:    acc.fiber    + (source.fiber      || 0),
    };
  }, { calories:0, protein:0, carbs:0, fat:0, fiber:0 });

  function openMeal(meal) {
    const logged       = logs.find(l => l.date === logDate)?.meals?.[meal];
    const defaultEntry = DAILY_DEFAULTS[meal];
    setActiveMeal({
      meal,
      isEdit: !!(logged?.ingredients?.length),
      ingredients: logged?.ingredients
        || defaultEntry?.ingredients?.map(i => ({ ...i }))
        || [{ name: "", amount: 100, unit: "g" }],
      notes: logged?.notes || "",
    });
  }
  function closeMeal() { setActiveMeal({ meal: null, ingredients: [], notes: "", isEdit: false }); }

  function autoCalcMeal() {
    const ingredients = activeMeal.ingredients;
    if (!ingredients?.length || !macroGoals) return;

    // Remaining targets = goals minus what's already logged in OTHER meals today
    const dayLogged = logs.find(l => l.date === logDate);
    let otherCal = 0, otherPro = 0;
    for (const [meal, data] of Object.entries(dayLogged?.meals || {})) {
      if (meal === activeMeal.meal || !data?.macros) continue;
      otherCal += data.macros.calories || 0;
      otherPro += data.macros.protein  || 0;
    }
    const targetPro = Math.max(0, (macroGoals.protein  || 0) - otherPro);
    const targetCal = Math.max(0, (macroGoals.calories || 0) - otherCal);

    const withN = ingredients.map(ing => ({
      ing,
      n: ing._nutrients || lookupNutrients(ing.name),
      amt: Number.isFinite(parseFloat(ing.amount)) ? parseFloat(ing.amount) : 100,
    }));
    if (!withN.some(x => x.n)) { showToast("No nutritional data — search ingredients first"); return; }

    // Protein-efficient = >= 25% of calories from protein (e.g. chicken, protein powder, eggs)
    // Everything else = carb/fat source → reduce high-carb items 20%, others unchanged
    const classified = withN.map(x => ({
      ...x,
      isPro: x.n ? (x.n.protein * 4) / Math.max(1, x.n.calories) >= 0.25 : false,
    }));

    // Step 1: calculate what fixed (non-protein) ingredients contribute
    const fixedCal = classified.filter(x => !x.isPro).reduce((s, x) => {
      if (!x.n) return s;
      return s + (x.n.calories * x.amt * (x.n.carbs > 25 ? 0.8 : 1)) / 100;
    }, 0);
    const fixedPro = classified.filter(x => !x.isPro).reduce((s, x) => {
      if (!x.n) return s;
      return s + (x.n.protein * x.amt * (x.n.carbs > 25 ? 0.8 : 1)) / 100;
    }, 0);

    // Step 2: scale protein sources to cover remaining protein need
    const proNeeded  = Math.max(0, targetPro - fixedPro);
    const calBudget  = Math.max(50, targetCal - fixedCal);
    const proItems   = classified.filter(x => x.isPro && x.n);
    const curProFromSrc = proItems.reduce((s, x) => s + (x.n.protein * x.amt) / 100, 0);
    const curCalFromSrc = proItems.reduce((s, x) => s + (x.n.calories * x.amt) / 100, 0);

    let scale = curProFromSrc > 0 ? proNeeded / curProFromSrc : 1;
    if (curCalFromSrc > 0) scale = Math.min(scale, calBudget / curCalFromSrc); // calorie cap
    scale = Math.max(0.1, Math.min(scale, 10));

    const newIngredients = classified.map(({ ing, n, isPro, amt }) => {
      if (!n) return ing;
      if (isPro) return { ...ing, amount: String(Math.max(10, Math.round(amt * scale))) };
      const r = n.carbs > 25 ? 0.8 : 1;
      return { ...ing, amount: String(Math.max(10, Math.round(amt * r))) };
    });

    setActiveMeal(a => ({ ...a, ingredients: newIngredients }));
    const result = calcMacros(newIngredients);
    const calOk = result.calories <= targetCal;
    showToast(`${calOk ? "✓" : "⚠ over cal"} ${result.protein}g protein · ${result.calories}kcal`);
  }
  function saveMeal() {
    const { meal, ingredients, notes } = activeMeal;
    if (!meal) return;
    const resolved = ingredients.map(ing => {
      if (ing._nutrients) return ing;
      const n = lookupNutrients(ing.name);
      if (n) return { ...ing, _nutrients: n, _source: "Local", _per: "per 100g" };
      return ing;
    });
    const existing = logs.find(l => l.date === logDate);
    const existingMeal = existing?.meals?.[meal];
    // If editing an already-logged meal, replace ingredients; otherwise append (supports top-ups)
    const merged  = (existingMeal?.logged && !activeMeal.isEdit) ? [...existingMeal.ingredients, ...resolved] : resolved;
    const macros  = calcMacros(merged);
    const updatedMeals = { ...(existing?.meals || {}), [meal]: { ingredients: merged, notes, macros, logged: true } };
    const updatedLogs = existing
      ? logs.map(l => l.date === logDate ? { ...l, meals: updatedMeals } : l)
      : [...logs, { id: Date.now(), date: logDate, meals: updatedMeals }];
    saveLogs(updatedLogs);
    showToast(activeMeal.isEdit ? `✓ ${meal} updated!` : `✓ ${meal} logged!`);
    closeMeal();
  }

  return (
<div style={{ display:"flex",flexDirection:"column",gap:18 }}>
  <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-end",flexWrap:"wrap",gap:12 }}>
    <div>
      <h1 style={{ fontSize:26,fontWeight:600,margin:0,letterSpacing:-0.5 }}>Meal Log</h1>
      <p style={{ color:"#999",margin:"4px 0 0",fontSize:14 }}>Tap a meal to log it — pre-filled from defaults</p>
    </div>
    <div><label style={lbl}>Date</label><input type="date" value={logDate} onChange={e=>setLogDate(e.target.value)} style={{...inp,width:"auto"}}/></div>
  </div>


  {/* Daily macro totals vs goals */}
  {(()=>{
    const totals = getDayTotals(logDate) || { calories:0, protein:0, carbs:0, fat:0, fiber:0 };
    const goals  = macroGoals || {};
    const macros = [
      { label:"Calories", unit:"kcal", val:totals.calories, goal:goals.calories, color:"#f97316" },
      { label:"Protein",  unit:"g",    val:totals.protein,  goal:goals.protein,  color:"#22c55e" },
      { label:"Carbs",    unit:"g",    val:totals.carbs,    goal:goals.carbs,    color:"#3b82f6" },
      { label:"Fat",      unit:"g",    val:totals.fat,      goal:goals.fat,      color:"#a855f7" },
      { label:"Fiber",    unit:"g",    val:totals.fiber,    goal:goals.fiber,    color:"#f59e0b" },
    ];
    return (
      <div style={{ background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:13,padding:"14px 18px" }}>
        <div style={{ fontSize:11,fontFamily:"'Space Mono',monospace",color:"#999",textTransform:"uppercase",letterSpacing:1.2,marginBottom:12 }}>
          Daily Total — {fmtDate(logDate)}
        </div>
        <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(120px,1fr))",gap:10 }}>
          {macros.map(m=>{
            const pct    = m.goal>0 ? Math.min((m.val/m.goal)*100,100) : 0;
            const remain = m.goal>0 ? m.goal - m.val : null;
            const over   = remain!==null && remain<0;
            return (
              <div key={m.label} style={{ background:"rgba(255,255,255,0.05)",borderRadius:10,padding:"10px 12px" }}>
                <div style={{ fontSize:10,fontFamily:"'Space Mono',monospace",color:"#aaa",textTransform:"uppercase",letterSpacing:1,marginBottom:6 }}>{m.label}</div>
                <div style={{ fontSize:20,fontWeight:700,color:m.color,fontFamily:"'Space Mono',monospace" }}>
                  {m.val}<span style={{ fontSize:11,fontWeight:400,color:"#888" }}>{m.unit}</span>
                </div>
                {m.goal>0 && (
                  <>
                    <div style={{ fontSize:11,color:over?"#f87171":"#aaa",marginTop:3 }}>
                      {over ? `+${Math.abs(remain)}${m.unit} over` : `${remain}${m.unit} left`}
                      <span style={{ color:"#666",marginLeft:4 }}>/ {m.goal}{m.unit}</span>
                    </div>
                    <div style={{ marginTop:6,height:3,borderRadius:2,background:"rgba(255,255,255,0.1)" }}>
                      <div style={{ height:"100%",borderRadius:2,width:`${pct}%`,background:over?"#ef4444":m.color,transition:"width 0.3s" }}/>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  })()}

  {/* Projected full-day total (logged + unlogged default meals) */}
  {Object.values(DAILY_DEFAULTS).length > 0 && (()=>{
    const goals = macroGoals || {};
    const loggedCount = MEALS.filter(m => dayLog?.meals?.[m]?.logged).length;
    const rows = [
      { label:"Calories", unit:"kcal", val:Math.round(projectedDay.calories), goal:goals.calories, color:"#f97316" },
      { label:"Protein",  unit:"g",    val:Math.round(projectedDay.protein),  goal:goals.protein,  color:"#22c55e" },
      { label:"Carbs",    unit:"g",    val:Math.round(projectedDay.carbs),    goal:goals.carbs,    color:"#3b82f6" },
      { label:"Fat",      unit:"g",    val:Math.round(projectedDay.fat),      goal:goals.fat,      color:"#a855f7" },
    ];
    return (
      <div style={{ background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.09)",borderRadius:13,padding:"14px 18px" }}>
        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,flexWrap:"wrap",gap:6 }}>
          <div style={{ fontSize:11,fontFamily:"'Space Mono',monospace",color:"#999",textTransform:"uppercase",letterSpacing:1.2 }}>
            Projected Day ({loggedCount}/{MEALS.length} logged)
          </div>
          <div style={{ fontSize:11,color:"#777",fontStyle:"italic" }}>unlogged meals estimated from defaults</div>
        </div>
        <div style={{ display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10 }}>
          {rows.map(m=>{
            const diff  = m.goal>0 ? m.val - m.goal : null;
            const over  = diff!==null && diff>0;
            const under = diff!==null && diff<0;
            const pct   = m.goal>0 ? Math.min((m.val/m.goal)*100,100) : 0;
            const statusColor = over?"#f87171":under&&Math.abs(diff)>m.goal*0.1?"#fbbf24":"#4ade80";
            return (
              <div key={m.label} style={{ background:"rgba(255,255,255,0.05)",borderRadius:10,padding:"10px 12px" }}>
                <div style={{ fontSize:10,fontFamily:"'Space Mono',monospace",color:"#aaa",textTransform:"uppercase",letterSpacing:1,marginBottom:5 }}>{m.label}</div>
                <div style={{ fontSize:18,fontWeight:700,color:m.color,fontFamily:"'Space Mono',monospace" }}>
                  {m.val}<span style={{ fontSize:10,fontWeight:400,color:"#888" }}>{m.unit}</span>
                </div>
                {m.goal>0 && (
                  <>
                    <div style={{ fontSize:11,color:statusColor,marginTop:3,fontWeight:500 }}>
                      {over  ? `+${diff}${m.unit} over`  :
                       under ? `${Math.abs(diff)}${m.unit} short` : "on target"}
                    </div>
                    <div style={{ marginTop:5,height:3,borderRadius:2,background:"rgba(255,255,255,0.1)" }}>
                      <div style={{ height:"100%",borderRadius:2,width:`${pct}%`,background:over?"#ef4444":pct>=90?m.color:"#f59e0b",transition:"width 0.3s" }}/>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  })()}

  {MEALS.map(meal=>{
    const logged     = logs.find(l=>l.date===logDate)?.meals?.[meal];
    const isOpen     = activeMeal.meal===meal;
    return (
      <div key={meal} style={{
        background: isOpen?"rgba(255,255,255,0.07)":"rgba(255,255,255,0.04)",
        border:`1px solid ${isOpen?"rgba(249,115,22,0.4)":logged?"rgba(34,197,94,0.25)":"rgba(255,255,255,0.1)"}`,
        borderRadius:15,overflow:"hidden",
      }}>
        <div style={{ padding:"15px 20px",display:"flex",justifyContent:"space-between",alignItems:"center" }}>
          <div onClick={()=>isOpen?closeMeal():openMeal(meal)} style={{ flex:1,cursor:"pointer" }}>
            <div style={{ fontWeight:600,fontSize:15,marginBottom:2 }}>{meal}</div>
            {logged ? (
              <div style={{ fontSize:11,color:"#22c55e",fontFamily:"'Space Mono',monospace" }}>✓ Logged · {logged.macros?.calories}kcal · {logged.macros?.protein}g protein</div>
            ) : (
              <div style={{ fontSize:12,color:"#777" }}>Tap to log</div>
            )}
          </div>
          <div style={{ display:"flex",alignItems:"center",gap:8 }}>
            {logged && !isOpen && (
              <button onClick={e=>{
                e.stopPropagation();
                if (!window.confirm(`Clear ${meal} from today's log?`)) return;
                const existing = logs.find(l=>l.date===logDate);
                if (!existing) return;
                const updatedMeals = { ...existing.meals };
                delete updatedMeals[meal];
                const updatedLogs = logs.map(l=>l.date===logDate ? { ...l, meals:updatedMeals } : l);
                saveLogs(updatedLogs);
                showToast(`${meal} cleared`);
              }} style={{ ...btn,padding:"4px 10px",fontSize:11,color:"#f87171",background:"rgba(248,113,113,0.08)",border:"1px solid rgba(248,113,113,0.2)" }}>✕ Clear</button>
            )}
            <span onClick={()=>isOpen?closeMeal():openMeal(meal)} style={{ color:isOpen?"#f97316":"#555",fontSize:16,cursor:"pointer" }}>{isOpen?"▲":"▼"}</span>
          </div>
        </div>

        {isOpen && (
          <div style={{ padding:"0 20px 20px",borderTop:"1px solid rgba(255,255,255,0.05)" }}>
            {DAILY_DEFAULTS[meal] && <div style={{ fontSize:12,color:"#888",marginTop:10,marginBottom:6,fontStyle:"italic" }}>
              Default: {DAILY_DEFAULTS[meal].description} — edit freely below
            </div>}
            <div style={{ marginTop:12 }}>
              <label style={lbl}>Ingredients</label>
              <IngredientEditor ingredients={activeMeal.ingredients} onChange={ing=>setActiveMeal(a=>({...a,ingredients:ing}))} customFoods={customFoods} logs={logs}/>
            </div>
            {activeMeal.ingredients.some(i=>i.name?.trim()) && (
              <div style={{ marginTop:12 }}>
                <label style={lbl}>Live Macro Preview</label>
                <MacroBar macros={liveMacros}/>
                {liveMacros.known === 0 && liveMacros.total > 0 && (
                  <div style={{ fontSize:11,color:"#999",marginTop:6,fontStyle:"italic" }}>
                    ⚠ No ingredients matched the nutrient database — try simpler names (e.g. "chicken breast", "brown rice", "prawns")
                  </div>
                )}
              </div>
            )}
            <div style={{ marginTop:12 }}>
              <label style={lbl}>Notes</label>
              <input style={inp} value={activeMeal.notes} placeholder="e.g. swapped prawns for soy mince" onChange={e=>setActiveMeal(a=>({...a,notes:e.target.value}))}/>
            </div>
            <div style={{ display:"flex",gap:9,marginTop:14,flexWrap:"wrap" }}>
              <button onClick={saveMeal} style={{ ...btn,padding:"10px 22px",fontSize:14,background:"linear-gradient(135deg,#f97316,#ec4899)",color:"#fff" }}>{activeMeal.isEdit ? `Save ${meal}` : `Log ${meal}`}</button>
              <button onClick={autoCalcMeal} title="Scale protein sources to hit remaining daily protein goal within calorie budget"
                style={{ ...btn,padding:"10px 16px",fontSize:13,background:"rgba(59,130,246,0.15)",color:"#60a5fa",border:"1px solid rgba(59,130,246,0.25)" }}>⚡ Auto-calc</button>
              <button onClick={closeMeal} style={{ ...btn,padding:"10px 16px",fontSize:13,background:"rgba(255,255,255,0.06)",color:"#777" }}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    );
  })}
</div>
  );
}
